/**
 * Application Footer Component for Cedar Heights Music Academy Admin System
 * 
 * This component provides the application footer with:
 * - Copyright information
 * - Version information
 * - Quick links
 * - Contact information
 * - Responsive design
 */

import React from 'react';
import {
  Box,
  Typography,
  Link,
  Divider,
  useTheme,
} from '@mui/material';

/**
 * Simple footer component with copyright and version information
 */
export const AppFooter: React.FC = () => {
  const theme = useTheme();
  const currentYear = new Date().getFullYear();

  return (
    <Box
      component="footer"
      sx={{
        backgroundColor: theme.palette.background.paper,
        borderTop: `1px solid ${theme.palette.divider}`,
        padding: theme.spacing(2, 3),
        marginTop: 'auto',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'flex-start', sm: 'center' },
          gap: theme.spacing(2),
        }}
      >
        {/* Copyright and Company Info */}
        <Box>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ marginBottom: { xs: theme.spacing(0.5), sm: 0 } }}
          >
            © {currentYear} Cedar Heights Music Academy. All rights reserved.
          </Typography>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: { xs: 'block', sm: 'none' } }}
          >
            Admin System v1.0.0
          </Typography>
        </Box>

        {/* Quick Links */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: theme.spacing(0.5), sm: theme.spacing(3) },
            alignItems: { xs: 'flex-start', sm: 'center' },
          }}
        >
          <Link
            href="#"
            variant="caption"
            color="text.secondary"
            underline="hover"
            onClick={(e) => {
              e.preventDefault();
              // TODO: Implement help/support functionality
            }}
          >
            Help & Support
          </Link>
          <Link
            href="#"
            variant="caption"
            color="text.secondary"
            underline="hover"
            onClick={(e) => {
              e.preventDefault();
              // TODO: Implement privacy policy
            }}
          >
            Privacy Policy
          </Link>
          <Link
            href="#"
            variant="caption"
            color="text.secondary"
            underline="hover"
            onClick={(e) => {
              e.preventDefault();
              // TODO: Implement terms of service
            }}
          >
            Terms of Service
          </Link>
        </Box>

        {/* Version Info (Desktop Only) */}
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: { xs: 'none', sm: 'block' } }}
        >
          Admin System v1.0.0
        </Typography>
      </Box>
    </Box>
  );
};

export default AppFooter;